import { useCallback, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { getAllDmsAndLBs } from '#src/app/pages/petitionDetails/shared/api';
import type { DecisionMakersAndLegislativeBodies } from '#src/app/pages/petitionDetails/shared/api';

import type { NormalizedDMsAndLBs } from '../../../../hooks/useDecisionMakerSection/types';
import { normalizeDmsAndLBs } from '../../../../hooks/useDecisionMakerSection/utils';
import { useTracking } from '../../../../hooks/useTracking';

type Result = ModelHookResult<
	{
		petitionAsk: string;
		totalDmCount: number;
		dmsAndLBs: NormalizedDMsAndLBs;
		loading: boolean;
		error: boolean;
	},
	{ handleViewAllClick: () => void }
>;

export function useViewAll(): Result {
	const utilityContext = useUtilityContext();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [allDmsAndLBs, setDmsAndLBs] = useState<DecisionMakersAndLegislativeBodies | undefined>(undefined);

	const {
		data: {
			petition: {
				ask,
				slug,
				dmsWithLegislativeBodiesConnection: { totalCount },
			},
		},
	} = usePetitionDetailsPageData();

	const { trackViewAllDMsClick } = useTracking();

	const getAll = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const data = await getAllDmsAndLBs(slug, utilityContext);
			setDmsAndLBs(data.nodes);
		} catch (caughtError) {
			// leave the existing (possibly empty) list unchanged and surface an inline error
			// so the spinner doesn't just silently stop with no feedback
			void utilityContext.errorReporter.report({
				error:
					caughtError instanceof Error
						? caughtError
						: new Error('Failed to load decision makers and legislative bodies', { cause: caughtError }),
				params: { slug },
			});
			setError(true);
		} finally {
			setLoading(false);
		}
	}, [slug, utilityContext]);

	const handleViewAllClick = useCallback(() => {
		trackViewAllDMsClick();
		void getAll();
	}, [trackViewAllDMsClick, getAll]);

	return {
		data: {
			petitionAsk: ask,
			dmsAndLBs: normalizeDmsAndLBs(allDmsAndLBs),
			totalDmCount: totalCount,
			loading,
			error,
		},
		actions: { handleViewAllClick },
	};
}
